import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  MenuItem, TextField, Tooltip, Typography,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { CountryPicker, Segmented, get, put, tokens, useCountries } from '@agribot/polygon-checks-ui'
import {
  useAddToCampaign, useCampaignRefresh, useMaterials, useParentCompanies, useReviewers, useSetAssignment, useSetMaterialIngredient, useStaff,
  useSuppliers, useTaxonomy,
  type AddToCampaign, type AddToCampaignResult, type Assignment, type Material, type Person,
} from '../api'

interface NewMaterial { key: number; code: string; name: string; kind: 'fixed' | 'blend'; commodityId: string; ingredientId: string; volume: string }
interface Contact { key: number; email: string; fullName: string }

const looksLikeEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())
const volumeOk = (v: string) => v.trim() !== '' && Number.isFinite(Number(v.replace(/[\s,]/g, ''))) && Number(v.replace(/[\s,]/g, '')) >= 0
const tonnes = (v: string) => Number(v.replace(/[\s,]/g, ''))
let nextKey = 1
const blankMaterial = (): NewMaterial => ({ key: nextKey++, code: '', name: '', kind: 'fixed', commodityId: '', ingredientId: '', volume: '' })
const blankContact = (): Contact => ({ key: nextKey++, email: '', fullName: '' })
const personLabel = (p: Person) => p.fullName ?? p.email ?? p.id.slice(0, 8)

function Section({ title, help, children }: { title: string; help?: ReactNode; children: ReactNode }) {
  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" component="h3">{title}</Typography>
        {help && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{help}</Typography>}
      </Box>
      {children}
    </Box>
  )
}

export type AssignmentDialogMode =
  | { kind: 'add'; inCampaign: string[] }
  | { kind: 'edit'; assignment: Assignment }

/**
 * A supplier in a campaign without a spreadsheet: added with its materials,
 * reviewers and contacts (what its rows of the import would say), or, for
 * one already in the campaign, its materials changed and contacts added.
 * Adding only adds; taking a material off is the edit's job, and says which
 * answers it would lose.
 */
export function AssignmentDialog({ campaignId, campaignOpen, mode, onClose, onDone }: {
  campaignId: string
  campaignOpen: boolean
  mode: AssignmentDialogMode
  onClose: () => void
  onDone: (message: string) => void
}) {
  const editing = mode.kind === 'edit' ? mode.assignment : null
  const { data: suppliers = [] } = useSuppliers()
  const { data: parents = [] } = useParentCompanies()
  const { data: countries = [] } = useCountries()
  const { data: taxonomy } = useTaxonomy()
  const { data: staff = [] } = useStaff()
  const commodities = (taxonomy?.commodities ?? []).filter((c) => c.active)
  const ingredientsOf = (commodityId: string | null) => (taxonomy?.ingredients ?? []).filter((f) => f.active && f.commodityId === commodityId)

  const [which, setWhich] = useState<'existing' | 'new'>('existing')
  const [supplierId, setSupplierId] = useState<string | null>(editing?.supplierId ?? null)
  const [name, setName] = useState('')
  const [country, setCountry] = useState('')
  const [ref, setRef] = useState('')
  const [parent, setParent] = useState('')
  const existing = editing ? true : which === 'existing'
  const chosen = existing ? supplierId : null

  const materialsQuery = useMaterials(chosen)
  const loadingMaterials = materialsQuery.isFetching
  const materials = useMemo(() => materialsQuery.data ?? [], [materialsQuery.data])
  const assignedIds = useMemo(() => new Set(editing?.materials.map((m) => m.id) ?? []), [editing])
  const [ticked, setTicked] = useState<Set<string>>(new Set(assignedIds))
  // Adding a supplier you have: all its current materials, to untick. Editing: what is assigned.
  useEffect(() => {
    if (!editing && chosen) setTicked(new Set(materials.filter((m) => m.active).map((m) => m.id)))
  }, [editing, chosen, materials])
  const [fresh, setFresh] = useState<NewMaterial[]>(() => (editing ? [] : [blankMaterial()]))
  useEffect(() => {
    // A supplier with materials of its own starts from them; a new one starts with a blank material.
    if (!editing) setFresh(existing && chosen && materials.length ? [] : [blankMaterial()])
  }, [editing, existing, chosen, materials.length])

  const { data: currentReviewers } = useReviewers(chosen)
  const [level1, setLevel1] = useState<Person[]>([])
  const [level2, setLevel2] = useState<Person[]>([])
  useEffect(() => {
    if (currentReviewers) { setLevel1(currentReviewers.level1); setLevel2(currentReviewers.level2) }
    else if (!chosen) { setLevel1([]); setLevel2([]) }
  }, [currentReviewers, chosen])

  const [contacts, setContacts] = useState<Contact[]>(() => (editing ? [] : [blankContact()]))

  // What the buyer buys of each ticked material in this campaign: this campaign's volume when it is
  // assigned already, else the material's current one; only a changed volume is sent.
  const [volumes, setVolumes] = useState<Record<string, string>>({})
  const baseVolume = (id: string) => editing?.materials.find((a) => a.id === id)?.annualVolumeT ?? materials.find((m) => m.id === id)?.annualVolumeT ?? 0
  const volumeOf = (id: string) => volumes[id] ?? String(baseVolume(id))
  const volumeChanged = (id: string) => volumeOk(volumeOf(id)) && tonnes(volumeOf(id)) !== baseVolume(id)

  // The ingredient each fixed material is: it counts towards that ingredient's need, and the supplier's answer starts from it.
  const [ingredientPicks, setIngredientPicks] = useState<Record<string, string>>({})
  const baseIngredient = (id: string) => materials.find((m) => m.id === id)?.ingredientId ?? ''
  const ingredientOf = (id: string) => ingredientPicks[id] ?? baseIngredient(id)
  const ingredientChanges = [...ticked].filter((id) => ingredientOf(id) !== baseIngredient(id))

  const add = useAddToCampaign(campaignId)
  const setAssignment = useSetAssignment(campaignId)
  const setIngredient = useSetMaterialIngredient(campaignId)
  const refresh = useCampaignRefresh(campaignId)
  const changeReviewers = (id: string, b: { level1: string[]; level2: string[] }) => put(`/suppliers/${id}/reviewers`, b)

  const freshFilled = fresh.filter((m) => m.code.trim() || m.name.trim() || m.volume.trim() || m.commodityId)
  const contactsFilled = contacts.filter((c) => c.email.trim() || c.fullName.trim())
  const losing = editing ? editing.materials.filter((m) => !ticked.has(m.id)) : []
  const losingAnswers = losing.filter((m) => m.answered)

  const problems: string[] = []
  if (!editing) {
    if (existing && !supplierId) problems.push('Choose the supplier.')
    if (!existing && (!name.trim() || !country)) problems.push('Give the new supplier a name and a country.')
  }
  for (const m of freshFilled) {
    if (!m.code.trim() || !m.name.trim()) problems.push('Each new material needs a code and a name.')
    else if (m.kind === 'fixed' && !m.commodityId) problems.push(`${m.code} is fixed: choose its commodity.`)
    else if (!volumeOk(m.volume)) problems.push(`${m.code}: the annual volume is a number of tonnes.`)
  }
  const codes = [...freshFilled.map((m) => m.code.trim().toLowerCase()), ...materials.map((m) => m.code.toLowerCase())]
  if (new Set(codes).size !== codes.length) problems.push('Two materials have the same code.')
  for (const id of ticked) {
    if (!volumeOk(volumeOf(id))) problems.push(`${materials.find((m) => m.id === id)?.code ?? 'A material'}: the volume is a number of tonnes.`)
  }
  if (ticked.size + freshFilled.length === 0) problems.push('Choose at least one material. To take the supplier out of the campaign, use Remove.')
  for (const c of contactsFilled) if (!looksLikeEmail(c.email)) problems.push(`${c.email || c.fullName}: give an email address.`)
  const firstProblem = problems[0]

  const reviewerIds = (v: Person[]) => v.map((p) => p.id)
  const reviewersChanged = Boolean(currentReviewers) && (
    reviewerIds(level1).join() !== reviewerIds(currentReviewers!.level1).join() || reviewerIds(level2).join() !== reviewerIds(currentReviewers!.level2).join())

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const newMaterials = freshFilled.map((m) => ({
        code: m.code.trim(), name: m.name.trim(), kind: m.kind, annualVolumeT: tonnes(m.volume),
        ...(m.kind === 'fixed' ? { commodityId: m.commodityId, ...(m.ingredientId ? { ingredientId: m.ingredientId } : {}) } : {}),
      }))
      // Ingredients first: assigning then starts each new answer from its material's.
      for (const m of ingredientChanges) await setIngredient.mutateAsync({ id: m, ingredientId: ingredientOf(m) || null })
      const newContacts = contactsFilled.map((c) => ({ email: c.email.trim(), ...(c.fullName.trim() ? { fullName: c.fullName.trim() } : {}) }))
      if (!editing) {
        const body: AddToCampaign = {
          supplier: existing
            ? { id: supplierId! }
            : { name: name.trim(), countryCode: country, ...(ref.trim() ? { externalRef: ref.trim() } : {}), ...(parent.trim() ? { parentCompany: parent.trim() } : {}) },
          materials: [...[...ticked].map((id) => ({ id, ...(volumeChanged(id) ? { annualVolumeT: tonnes(volumeOf(id)) } : {}) })), ...newMaterials],
          // Reviewers it has already stay either way; ones taken off here go too.
          reviewers: { level1: reviewerIds(level1), level2: reviewerIds(level2) },
          contacts: newContacts,
        }
        if (existing && reviewersChanged) await changeReviewers(supplierId!, { level1: reviewerIds(level1), level2: reviewerIds(level2) })
        const r = await add.mutateAsync(body)
        return summary(existing ? suppliers.find((s) => s.id === supplierId)?.name ?? 'The supplier' : name.trim(), r, campaignOpen)
      }
      // Editing: add what is new first, so a failure leaves the assignment as it was.
      const id = editing.supplierId
      const changed = Object.fromEntries([...ticked].filter(volumeChanged).map((m) => [m, tonnes(volumeOf(m))]))
      let added: AddToCampaignResult | null = null
      if (newMaterials.length || newContacts.length) {
        added = await add.mutateAsync({
          supplier: { id },
          materials: [...[...ticked].map((m) => ({ id: m, ...(m in changed ? { annualVolumeT: changed[m] } : {}) })), ...newMaterials],
          contacts: newContacts,
        })
      }
      const newlyTicked = [...ticked].some((m) => !assignedIds.has(m))
      if (losing.length || (!added && (newlyTicked || Object.keys(changed).length))) {
        const keep = new Set(ticked)
        if (newMaterials.length) {
          const all = (await get<{ items: Material[] }>(`/materials?supplierId=${id}`)).items
          for (const m of all) if (newMaterials.some((n) => n.code.toLowerCase() === m.code.toLowerCase())) keep.add(m.id)
        }
        // Volumes went with the add when there was one.
        await setAssignment.mutateAsync({ supplierId: id, materialIds: [...keep], volumes: added ? undefined : changed })
      }
      if (reviewersChanged) await changeReviewers(id, { level1: reviewerIds(level1), level2: reviewerIds(level2) })
      const parts = [
        newMaterials.length ? `${newMaterials.length} material${newMaterials.length === 1 ? '' : 's'} added` : null,
        losing.length ? `${losing.length} taken off` : null,
        Object.keys(changed).length ? `${Object.keys(changed).length} volume${Object.keys(changed).length === 1 ? '' : 's'} changed` : null,
        ingredientChanges.length ? `${ingredientChanges.length} ingredient${ingredientChanges.length === 1 ? '' : 's'} set` : null,
        added?.logins.added ? `${added.logins.added} contact${added.logins.added === 1 ? '' : 's'} added` : null,
        added?.emailsQueued ? `${added.emailsQueued} emailed a link` : null,
        reviewersChanged ? 'reviewers changed' : null,
      ].filter(Boolean)
      return `${editing.supplier}: ${parts.join(', ') || 'saved'}.`
    },
    // Reviewers changed on their own route: the campaign's table shows them too.
    onSuccess: (message) => { void refresh(); onDone(message); onClose() },
  })

  const reviewerPicker = (title: string, role: 'buyer' | 'regional_lead', value: Person[], onChange: (v: Person[]) => void) => (
    <Autocomplete
      multiple
      options={staff}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={personLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, p) => (
        <Box component="li" {...props} key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <span>{personLabel(p)}</span>
          {!p.roles?.includes(role) && <Typography variant="caption" sx={{ color: tokens.color.warning }}>no {role.replace('_', ' ')} role</Typography>}
        </Box>
      )}
      renderTags={(v, getTagProps) => v.map((p, i) => <Chip {...getTagProps({ index: i })} key={p.id} label={personLabel(p)} size="small" />)}
      renderInput={(params) => <TextField {...params} label={title} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
    />
  )

  // A long form: Escape or a stray click outside must not throw away what was typed.
  const dirty = Boolean(freshFilled.length || contactsFilled.length || name || country || ref || parent || reviewersChanged
    || Object.keys(volumes).length || ingredientChanges.length
    || (editing ? losing.length || [...ticked].some((m) => !assignedIds.has(m)) : supplierId))
  const options = suppliers.filter((s) => mode.kind === 'add' && !mode.inCampaign.includes(s.id))
  const busy = save.isPending

  return (
    <Dialog open onClose={busy || dirty ? undefined : onClose} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); if (!firstProblem) save.mutate() }}>
        <DialogTitle>{editing ? `Change ${editing.supplier}` : 'Add a supplier to the campaign'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 6, pt: '8px !important' }}>
          {!editing && (
            <Section title="Supplier">
              <Segmented
                value={which}
                ariaLabel="Which supplier"
                onChange={(v) => setWhich(v as 'existing' | 'new')}
                options={[{ value: 'existing', label: 'One you have' }, { value: 'new', label: 'A new supplier' }]}
              />
              {which === 'existing' ? (
                <Autocomplete
                  size="small"
                  options={options}
                  value={options.find((s) => s.id === supplierId) ?? null}
                  onChange={(_, s) => setSupplierId(s?.id ?? null)}
                  getOptionLabel={(s) => s.name}
                  renderOption={(props, s) => (
                    <Box component="li" {...props} key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                      <span>{s.name}</span>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.country_code ?? ''}</Typography>
                    </Box>
                  )}
                  noOptionsText="Every supplier you have is in this campaign already."
                  renderInput={(params) => <TextField {...params} label="Supplier (legal entity)" autoFocus InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
                />
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <TextField size="small" label="Name (legal entity)" value={name} onChange={(e) => setName(e.target.value)} autoFocus InputLabelProps={{ shrink: true }} />
                  <CountryPicker countries={countries} value={country} onChange={setCountry} label="Country" />
                  <TextField size="small" label="Your reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)}
                    helperText="E.g. its SAP vendor number." InputLabelProps={{ shrink: true }} />
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={parents.map((p) => p.name)}
                    inputValue={parent}
                    onInputChange={(_, v) => setParent(v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Parent company (optional)" helperText="Choose one, or type a new name to create it."
                        InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
                    )}
                  />
                </Box>
              )}
            </Section>
          )}

          <Section title="Materials" help="What the supplier answers for in this campaign, and how many tonnes of each you buy from it a year in this campaign. Give a fixed material its ingredient when you know it: it counts towards that ingredient’s need, and the supplier confirms it. Materials answered and approved last year arrive pre-filled.">
            {existing && chosen && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {loadingMaterials && !materials.length && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading its materials…</Typography>}
                {!loadingMaterials && !materials.length && <Typography variant="body2" sx={{ color: 'text.secondary' }}>It has no materials yet: add one below.</Typography>}
                {materials.map((m) => {
                  const answered = editing?.materials.find((a) => a.id === m.id)?.answered
                  const off = assignedIds.has(m.id) && !ticked.has(m.id)
                  return (
                    <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 3, minHeight: 44 }}>
                      <FormControlLabel
                        sx={{ flex: 1, mr: 0 }}
                        control={<Checkbox size="small" checked={ticked.has(m.id)} onChange={(e) => {
                          const next = new Set(ticked)
                          if (e.target.checked) next.add(m.id); else next.delete(m.id)
                          setTicked(next)
                        }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11, color: 'text.secondary', minWidth: 56 }}>{m.code}</Box>
                            <span>{m.name}</span>
                            <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>· {m.kind === 'blend' ? 'blend' : m.commodity}</Box>
                            {!m.active && <Chip size="small" label="Retired" />}
                            {off && answered && <Box component="span" sx={{ color: tokens.color.danger, fontSize: 12, fontWeight: 500 }}>its answer will be deleted</Box>}
                          </Box>
                        }
                      />
                      {ticked.has(m.id) && m.kind === 'fixed' && (
                        <TextField
                          size="small" select value={ingredientOf(m.id)}
                          onChange={(e) => setIngredientPicks({ ...ingredientPicks, [m.id]: e.target.value })}
                          SelectProps={{ displayEmpty: true, SelectDisplayProps: { 'aria-label': `${m.code}: ingredient`, 'aria-labelledby': undefined } as object, renderValue: (v) => (v ? ingredientsOf(m.commodityId).find((f) => f.id === v)?.name ?? '' : <Box component="span" sx={{ color: 'text.disabled' }}>Ingredient…</Box>) }}
                          sx={{ width: 200 }}
                        >
                          <MenuItem value=""><em>Not known</em></MenuItem>
                          {ingredientsOf(m.commodityId).map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                        </TextField>
                      )}
                      {ticked.has(m.id) ? (
                        <TextField
                          size="small"
                          value={volumeOf(m.id)}
                          onChange={(e) => setVolumes({ ...volumes, [m.id]: e.target.value })}
                          error={!volumeOk(volumeOf(m.id))}
                          inputProps={{ inputMode: 'decimal', 'aria-label': `${m.code}: tonnes bought a year` }}
                          InputProps={{ endAdornment: <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, pl: 1, whiteSpace: 'nowrap' }}>t a year</Box> }}
                          sx={{ width: 170 }}
                        />
                      ) : (
                        <Box component="span" sx={{ width: 170, color: 'text.disabled', fontSize: 12, textAlign: 'right' }}>{baseVolume(m.id).toLocaleString('en-GB')} t</Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
            {fresh.map((m, i) => (
              <Box key={m.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '96px minmax(0, 1.2fr) 96px minmax(0, 1fr) minmax(0, 1fr) 104px 32px' }, gap: 2, alignItems: 'start' }}>
                <TextField size="small" label="Code" value={m.code} onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, code: e.target.value } : x))} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Name" value={m.name} onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, name: e.target.value } : x))} InputLabelProps={{ shrink: true }} />
                <TextField size="small" select label="Kind" value={m.kind} onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, kind: e.target.value as 'fixed' | 'blend', commodityId: '' } : x))} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="fixed">Fixed</MenuItem>
                  <MenuItem value="blend">Blend</MenuItem>
                </TextField>
                <TextField size="small" select label="Commodity" value={m.kind === 'blend' ? '' : m.commodityId} disabled={m.kind === 'blend'}
                  helperText={m.kind === 'blend' ? 'A blend mixes several.' : undefined}
                  onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, commodityId: e.target.value, ingredientId: '' } : x))} InputLabelProps={{ shrink: true }}>
                  {commodities.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Ingredient (optional)" value={m.ingredientId} disabled={m.kind === 'blend' || !m.commodityId}
                  helperText={m.kind === 'blend' ? 'The supplier says.' : undefined}
                  onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, ingredientId: e.target.value } : x))} InputLabelProps={{ shrink: true }}>
                  <MenuItem value=""><em>Not known</em></MenuItem>
                  {ingredientsOf(m.commodityId).map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Bought a year (t)" value={m.volume} inputProps={{ inputMode: 'decimal' }}
                  onChange={(e) => setFresh(fresh.map((x) => x.key === m.key ? { ...x, volume: e.target.value } : x))} InputLabelProps={{ shrink: true }} />
                <Tooltip title="Remove this row">
                  <IconButton aria-label={`Remove new material ${i + 1}`} onClick={() => setFresh(fresh.filter((x) => x.key !== m.key))} sx={{ mt: 0.5 }}><X size={16} /></IconButton>
                </Tooltip>
              </Box>
            ))}
            <Box><Button variant="text" startIcon={<Plus size={16} />} onClick={() => setFresh([...fresh, blankMaterial()])}>Add a new material</Button></Box>
            {losingAnswers.length > 0 && (
              <Alert severity="warning">
                Taking off {losingAnswers.map((m) => m.code).join(', ')} deletes what the supplier has answered for {losingAnswers.length === 1 ? 'it' : 'them'} in this campaign.
              </Alert>
            )}
          </Section>

          <Section title="Reviewers" help="The supplier’s reviewers, in every campaign it is in.">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              {reviewerPicker('First review: commercial buyer', 'buyer', level1, setLevel1)}
              {reviewerPicker('Final review: regional lead', 'regional_lead', level2, setLevel2)}
            </Box>
          </Section>

          <Section
            title="Who answers for it"
            help={campaignOpen
              ? 'Each contact signs in to the supplier portal. The campaign is open, so new contacts are emailed a link when you save.'
              : 'Each contact signs in to the supplier portal. They are emailed a link when the campaign opens.'}
          >
            {editing && editing.logins.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {editing.logins.map((l) => <Chip key={l.id} size="small" label={l.fullName && l.email && l.fullName !== l.email ? `${l.fullName} · ${l.email}` : l.email ?? l.fullName} />)}
              </Box>
            )}
            {contacts.map((c, i) => (
              <Box key={c.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr) 32px' }, gap: 2, alignItems: 'start' }}>
                <TextField size="small" label="Name" value={c.fullName} onChange={(e) => setContacts(contacts.map((x) => x.key === c.key ? { ...x, fullName: e.target.value } : x))} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Email" type="email" value={c.email} onChange={(e) => setContacts(contacts.map((x) => x.key === c.key ? { ...x, email: e.target.value } : x))} InputLabelProps={{ shrink: true }} />
                <Tooltip title="Remove this row">
                  <IconButton aria-label={`Remove contact ${i + 1}`} onClick={() => setContacts(contacts.filter((x) => x.key !== c.key))} sx={{ mt: 0.5 }}><X size={16} /></IconButton>
                </Tooltip>
              </Box>
            ))}
            <Box><Button variant="text" startIcon={<Plus size={16} />} onClick={() => setContacts([...contacts, blankContact()])}>{editing ? 'Add a contact' : 'Add another contact'}</Button></Box>
          </Section>

          {save.isError && <Alert severity="error">{(save.error as Error).message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 3 }}>
          {firstProblem && <Typography variant="body2" sx={{ color: 'text.secondary', mr: 'auto' }}>{firstProblem}</Typography>}
          <Button variant="text" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={Boolean(firstProblem) || busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add to campaign'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function summary(supplier: string, r: AddToCampaignResult, open: boolean): string {
  const parts = [
    `${r.assignments.added} material${r.assignments.added === 1 ? '' : 's'} assigned`,
    r.materials.added ? `${r.materials.added} new` : null,
    r.prefilled ? `${r.prefilled} pre-filled from last year` : null,
    r.logins.added ? `${r.logins.added} contact${r.logins.added === 1 ? '' : 's'} added` : null,
    open ? (r.emailsQueued ? `${r.emailsQueued} emailed a link` : null) : 'contacts are emailed when the campaign opens',
  ].filter(Boolean)
  return `${supplier} added: ${parts.join(', ')}.`
}
